import { WardrobeCart } from './mg-wardrobe-cart.js?v=20261007-availability-1';

function mappedData(value, assets) {
  if (Array.isArray(value)) return value.map(item => mappedData(item, assets));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, mappedData(item, assets)]));
  if (typeof value === 'string' && value.startsWith('mgasset:')) {
    const image = assets[value.slice(8)];
    if (!image) throw new Error('Unmapped image');
    return image;
  }
  return value;
}

function mountCart(host, scope, config) {
  const dialog = scope.getElementById('review-dialog');
  const actions = document.createElement('div');
  actions.className = 'mg-cart-actions';
  const status = document.createElement('p');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  actions.append(status);
  dialog.querySelector('.dialog-content').append(actions);
  const note = dialog.querySelector('.dialog-note');
  if (!config.cartEnabled) {
    status.textContent = config.labels.preview_only;
    return;
  }
  note.textContent = config.labels.price_note;
  const cart = new WardrobeCart(config);
  host.cart = cart;
  const total = document.createElement('p');
  const check = document.createElement('button');
  const add = document.createElement('button');
  const review = document.createElement('a');
  check.type = add.type = 'button';
  check.className = add.className = 'review-button dialog-button';
  check.textContent = config.labels.check;
  add.textContent = config.labels.add;
  add.hidden = true;
  review.textContent = config.labels.review;
  review.href = cart.cartUrl.href;
  review.hidden = true;
  actions.append(total, check, add, review);
  const showPrices = result => {
    const formatter = new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: result.currency, currencyDisplay: 'code' });
    total.textContent = `${config.labels.subtotal}: ${formatter.format(result.rows.reduce((sum, row) => sum + row.price, 0) / 100)}`;
    const list = scope.getElementById('review-list');
    list.replaceChildren(...result.rows.map(row => {
      const item = document.createElement('li');
      item.className = 'review-item';
      item.textContent = row.title;
      const detail = document.createElement('small');
      detail.textContent = `${row.colour} · ${row.size} · ${formatter.format(row.price / 100)}`;
      item.append(detail);
      return item;
    }));
  };
  scope.getElementById('selection-form').addEventListener('submit', () => {
    if (cart.busy || cart.uncertain) { if (cart.approved) showPrices(cart.approved); return; }
    if (cart.added && !cart.resetAfterConfirmedAdd()) return;
    cart.approved = null;
    total.textContent = status.textContent = '';
    add.hidden = true;
    review.hidden = true;
    check.hidden = false;
    check.disabled = false;
  });
  check.addEventListener('click', async () => {
    if (cart.busy || cart.uncertain || cart.added) return;
    check.disabled = true;
    add.hidden = true;
    status.textContent = config.labels.checking;
    try {
      showPrices(await cart.prepare(host.wardrobe.getSelectionEntries()));
      status.textContent = config.labels.ready;
      add.hidden = false;
      add.disabled = false;
    } catch {
      total.textContent = '';
      status.textContent = config.labels.check_error;
    } finally { check.disabled = cart.uncertain || cart.added; }
  });
  add.addEventListener('click', async () => {
    if (cart.busy || cart.uncertain || cart.added) return;
    check.disabled = add.disabled = true;
    status.textContent = config.labels.adding;
    try {
      const result = await cart.add(host.wardrobe.getSelectionEntries());
      if (result.status === 'changed') {
        showPrices(result);
        status.textContent = config.labels.changed;
      } else {
        showPrices(cart.approved);
        status.textContent = config.labels.added;
        review.hidden = false;
        check.hidden = true;
        add.hidden = true;
      }
    } catch {
      if (cart.uncertain && cart.approved) showPrices(cart.approved);
      status.textContent = cart.uncertain ? config.labels.uncertain : config.labels.check_error;
      review.hidden = !cart.uncertain;
      if (!cart.uncertain) { cart.approved = null; add.hidden = true; }
    } finally { check.disabled = add.disabled = cart.uncertain || cart.added; }
  });
}

if (!customElements.get('mg-wardrobe')) {
  customElements.define('mg-wardrobe', class extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dataset.ready = 'false';
      this.setAttribute('aria-busy', 'true');
      let config;
      try {
        config = JSON.parse(this.querySelector('[data-mg-configuration]').textContent);
        const response = await fetch(config.dataUrl, { credentials: 'omit' });
        if (!response.ok) throw new Error('Wardrobe data unavailable');
        const payload = mappedData(await response.json(), config.assets);
        const scope = this.attachShadow({ mode: 'open' });
        const template = document.createElement('template');
        template.innerHTML = payload.markup;
        const style = document.createElement('link');
        style.rel = 'stylesheet';
        style.href = config.cssUrl;
        const styleReady = new Promise((resolve, reject) => { style.addEventListener('load', resolve, { once: true }); style.addEventListener('error', reject, { once: true }); });
        const mount = document.createElement('div');
        mount.hidden = true;
        mount.inert = true;
        mount.append(template.content.cloneNode(true));
        const loading = document.createElement('p');
        loading.setAttribute('role', 'status');
        loading.textContent = config.labels.loading;
        scope.append(style, loading, mount);
        for (const image of scope.querySelectorAll('[data-mg-asset]')) {
          const src = config.assets[image.dataset.mgAsset];
          if (!src) throw new Error('Unmapped static image');
          image.src = src;
          image.removeAttribute('data-mg-asset');
        }
        for (const [id, data] of Object.entries(payload.data)) {
          const element = document.createElement('script');
          element.type = 'application/json';
          element.id = id;
          element.textContent = JSON.stringify(data);
          scope.append(element);
        }
        this.stylingAssets = payload.stylingAssets;
        await styleReady;
        runWardrobe(this, scope, new Set(Object.values(config.assets)));
        mountCart(this, scope, config);
        mount.inert = false;
        mount.hidden = false;
        loading.remove();
        this.dataset.ready = 'true';
        this.removeAttribute('aria-busy');
        this.querySelector('[data-mg-loading]').hidden = true;
        this.querySelector('[data-mg-fallback]').hidden = true;
      } catch {
        this.dataset.ready = 'false';
        this.removeAttribute('aria-busy');
        if (this.shadowRoot) this.shadowRoot.replaceChildren(document.createElement('slot'));
        this.querySelector('[data-mg-loading]').textContent = config?.labels?.unavailable || 'The dressing room could not load.';
      }
    }
  });
}

function runWardrobe(host,scope,allowedImages){

    'use strict';
    
    (() => {
      function freezeData(value) { if (value && typeof value === 'object' && !Object.isFrozen(value)) { Object.values(value).forEach(freezeData); Object.freeze(value); } return value; }
      const catalog = freezeData(JSON.parse(scope.getElementById('catalog-data').textContent));
      const measurementCatalog = freezeData(JSON.parse(scope.getElementById('measurement-data').textContent));
      const stylingAssets = freezeData(host.stylingAssets);
      const previewCatalog = freezeData(JSON.parse(scope.getElementById('preview-data').textContent));
      const products = catalog.products || [];
      const state = {group:'men',outfit:'set',build:'regular',cohort:'adult',colour:'',bottomColour:'',topSize:'',bottomSize:'',unit:'cm'};
      const groupNames = {men:'Men',women:'Women',boys:'Boys',girls:'Girls'};
      const elements = Object.fromEntries(['canvas','canvas-label','preview-frame','preview-photo','reference-photos','product-composition','look-heading','preview-status','coverage-note','outfit','colour-choices','top-colour-label','bottom-colour-field','bottom-colour-choices','top-name','top-label','top-size','top-measurements','bottom-card','bottom-name','bottom-size','bottom-measurements','measurement-status','measurement-notes','units','review-button','selection-status','review-dialog','review-copy','review-list','garment-size-row','cohort','cohort-field','robe-option','model-status'].map(id => [id,scope.getElementById(id)]));
      const normalize = value => String(value || '').trim().toLowerCase().replace(/grey/g,'gray').replace(/[™®]/g,'').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

      const localAssetPath = value => typeof value === 'string' && allowedImages.has(value);
      const categoryAliases = {tee:'tshirt','t-shirt':'tshirt',tshirt:'tshirt',sweatshirt:'sweatshirt',crew:'sweatshirt',sweatpants:'sweatpants',pants:'sweatpants',robe:'robe'};
      function productCategory(product) { return categoryAliases[normalize(product.category)] || normalize(product.category); }
      function garment(category) { return products.find(product => (product.group === state.group || (category === 'robe' && product.group === 'all')) && productCategory(product) === category); }
      function pieces() { return {top:garment(state.outfit === 'set' ? 'sweatshirt':state.outfit === 'tshirt-pants' ? 'tshirt':state.outfit === 'pants' ? 'sweatpants':state.outfit),bottom:['set','tshirt-pants'].includes(state.outfit) ? garment('sweatpants'):null}; }
      function colourKey(colour) { return normalize(colour.id || colour.label); }
      function colourOf(product, key) { return product && (product.colors || []).find(colour => colourKey(colour) === key); }
      function sizeValues(colour) { return (colour && colour.sizes || []).map(size => typeof size === 'string' ? size:size.label || size.size).filter(Boolean); }
      function availableColours() {
        const {top,bottom} = pieces();
        return (top && top.colors || []).filter(colour => state.outfit !== 'set' || !bottom || (bottom.colors || []).some(other => colourKey(other) === colourKey(colour)));
      }
      function selectedColour(product) { return colourOf(product,state.outfit === 'tshirt-pants' && productCategory(product || {}) === 'sweatpants' ? state.bottomColour:state.colour); }
      function setText(element, text) { element.textContent = text; }
      function option(value, text) { const element = document.createElement('option'); element.value = value; element.textContent = text; return element; }
      function sizeOptions(select, product, selected) {
        const values = sizeValues(selectedColour(product));
        select.replaceChildren(option('','Choose size'), ...values.map(size => option(size,size)));
        select.value = values.includes(selected) ? selected:'';
        select.disabled = !values.length;
        select.setAttribute('aria-label',`${product && product.title || 'Garment'} size`);
        return select.value;
      }
      function swatch(colour) {
        if (/^#[0-9a-f]{3,8}$/i.test(colour.swatch || '')) return colour.swatch;
        const key = colourKey(colour);
        return key.includes('white') ? '#fff':key.includes('gray') ? '#d0d0cf':key.includes('brown') ? '#796b5f':key.includes('tan') ? '#b7ab93':key.includes('blue') ? '#577185':'#393937';
      }
      function updateColours() {
        const choices = availableColours();
        if (!choices.some(colour => colourKey(colour) === state.colour)) state.colour = colourKey(choices.find(colour => colourKey(colour).includes('gray')) || choices[0] || {});
        const mixed = state.outfit === 'tshirt-pants'; const bottom = pieces().bottom; const bottomChoices = mixed && bottom ? bottom.colors || []:[];
        state.bottomColour = mixed ? bottomChoices.some(colour => colourKey(colour) === state.bottomColour) ? state.bottomColour:colourKey(bottomChoices.find(colour => colourKey(colour).includes('gray')) || bottomChoices[0] || {}):'';
        elements['bottom-colour-field'].hidden = !mixed;
        setText(elements['top-colour-label'],mixed ? 'T-shirt colour':state.outfit === 'set' ? 'Matching set colour':'Garment colour');
        renderColourChoices(elements['colour-choices'],choices,'top'); renderColourChoices(elements['bottom-colour-choices'],bottomChoices,'bottom');
      }
      function renderColourChoices(target, choices, side) {
        target.replaceChildren(); const selected = side === 'bottom' ? state.bottomColour:state.colour;
        choices.forEach(colour => {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'colour-choice'; button.dataset.colour = colourKey(colour); button.setAttribute('aria-pressed',String(button.dataset.colour === selected));
          const chip = document.createElement('span'); chip.className = 'swatch'; chip.style.setProperty('--swatch',swatch(colour)); chip.setAttribute('aria-hidden','true');
          button.append(chip,document.createTextNode(colour.label || colour.id));
          button.addEventListener('click',() => {
            if ((side === 'bottom' ? state.bottomColour:state.colour) === button.dataset.colour) return;
            if (side === 'bottom') { state.bottomColour = button.dataset.colour; }
            else { state.colour = button.dataset.colour; }
            render(); const focusTarget = [...target.children].find(child => child.dataset.colour === button.dataset.colour); if (focusTarget) focusTarget.focus();
          });
          target.append(button);
        });
        if (!choices.length) setText(target,'Colour information is unavailable.');
      }
      function chartFor(product) {
        const measuredProduct = product && (measurementCatalog.products || []).find(entry => entry.handle === product.handle);
        return product && (measurementCatalog.charts || []).find(chart => (measuredProduct && chart.id === measuredProduct.measurement_chart_id) || (chart.productId && String(chart.productId) === String(product.id)) || (chart.productHandle && chart.productHandle === product.handle) || (chart.productHandles || []).includes(product.handle));
      }
      function formatValue(number) { return Number.isInteger(number) ? String(number):String(Number(number.toFixed(2))); }
      function measurementValue(value, originalUnit) {
        if (typeof value === 'object' && value !== null) {
          const exact = value[state.unit]; if (typeof exact === 'number') return `${formatValue(exact)} ${state.unit}`;
          originalUnit = typeof value.cm === 'number' ? 'cm':'in'; value = value[originalUnit];
        }
        if (typeof value !== 'number' || !Number.isFinite(value)) return null;
        const converted = state.unit === originalUnit ? value:state.unit === 'in' ? value / 2.54:value * 2.54;
        return `${formatValue(converted)} ${state.unit}`;
      }
      function renderMeasurements(product, selectedSize, target) {
        target.replaceChildren();
        if (!selectedSize) { setText(target,'Select a size for measurements.'); return {selected:false,complete:false,notes:[]}; }
        const chart = chartFor(product);
        const row = chart && (chart.rows || chart.values_by_size || []).find(row => String(row.size) === selectedSize);
        if (!row || !row.measurements) { setText(target,'Measurements are unavailable for this size.'); return {selected:true,complete:false,notes:chart && chart.notes || []}; }
        const list = document.createElement('dl');
        Object.entries(row.measurements).forEach(([label,value]) => {
          const text = measurementValue(value,chart.unit || 'cm'); if (text === null) return;
          const wrap = document.createElement('div'); wrap.className = 'measure-row'; const term = document.createElement('dt'); term.textContent = (chart.dimensions || []).find(dimension => dimension.key === label)?.label || label; const definition = document.createElement('dd'); definition.textContent = text; wrap.append(term,definition); list.append(wrap);
        });
        if (!list.children.length) { setText(target,'Measurements are unavailable for this size.'); return {selected:true,complete:false,notes:chart.notes || []}; }
        target.append(list);
        const notes = [...(Array.isArray(chart.notes) ? chart.notes:[]), 'Finished-garment measurements, not body measurements. Widths are measured flat; sizes do not imply model dimensions.'];
        if (chart.tolerance && typeof chart.tolerance.value === 'number') notes.push(`Supplier chart tolerance: ${chart.tolerance.published_notation || `±${chart.tolerance.value} ${chart.tolerance.unit}`}.`);
        return {selected:true,complete:true,notes};
      }
      function addComposition(product) {
        const colour = selectedColour(product); const image = colour && colour.image;
        const figure = document.createElement('figure'); figure.className = 'composition-item';
        const caption = document.createElement('figcaption'); caption.textContent = `${product.title} · ${colour && (colour.label || colour.id) || 'Colour information pending'}`;
        if (localAssetPath(image)) { const photo = document.createElement('img'); photo.src = image; photo.alt = colour.alt || `${product.title}, ${colour.label || colour.id}, current catalogue image.`; figure.append(photo); }
        else { const missing = document.createElement('p'); missing.className = 'preview-status'; missing.textContent = 'Product photograph unavailable.'; figure.append(missing); }
        figure.append(caption); elements['product-composition'].append(figure);
      }
      function resetPreviewCrop() {
        elements['preview-photo'].removeAttribute('style'); elements['preview-frame'].removeAttribute('style'); elements['preview-frame'].dataset.cropped = 'false'; delete elements['preview-frame'].dataset.cohort;
      }
      function showProductFallback(message) {
        elements.canvas.dataset.render = 'product'; elements['preview-photo'].hidden = true; elements['preview-frame'].hidden = true; elements['product-composition'].hidden = false; resetPreviewCrop();
        setText(elements['canvas-label'],'Current catalogue images'); setText(elements['preview-status'],message || 'Product photographs for this combination.');
      }
      const norm = value => String(value || '').toLowerCase().replace(/grey/g,'gray').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');
      function configureBuilds(youth) {
        const buttons = [...scope.querySelectorAll('[data-build]')];
        if (youth && state.build === 'regular') state.build = 'lean';
        scope.querySelector('.build-choices').dataset.youth = String(youth);
        buttons.forEach(button => { button.hidden = youth && button.dataset.build === 'regular'; button.setAttribute('aria-pressed',String(button.dataset.build === state.build)); });
      }
      function stylingRoute(key) {
        const models = stylingAssets.models.filter(row => row.group === key.group && row.cohort === key.cohort && row.build === key.build);
        if (models.length !== 1) return null;
        const outfit = ['set','sweatshirt'].includes(key.outfit) ? 'crew-set':key.outfit === 'robe' ? 'robe':'tee-set';
        const colour = key.outfit === 'pants' ? 'white':key.colour;
        const bottomColour = outfit === 'robe' ? '':key.outfit === 'tshirt-pants' ? key.bottomColour:key.outfit === 'pants' || outfit === 'crew-set' ? key.colour:'gray';
        return {modelId:models[0].modelId,group:key.group,cohort:key.cohort,build:key.build,outfit,colour,bottomColour};
      }
      function matchesGarmentRoute(row, route) {
        return ['modelId','group','cohort','build','outfit','colour','bottomColour'].every(field => norm(row[field]) === norm(route[field]));
      }
      function clothingLook(row) {
        return Boolean(row);
      }
      function refinedStylingLook(key) {
        const route = stylingRoute(key); if (!route) return null;
        const active = stylingAssets.activeCasts[route.modelId] || null;
        const sources = active ? stylingAssets.completeLooks:stylingAssets.looks;
        const matches = stylingAssets.refinedLooks.filter(row => clothingLook(row) && row.visualReviewPassed === true && row.reviewBindingValidated === true && row.renderMode === 'complete-on-model' && matchesGarmentRoute(row,route) && (row.sourceCastId || null) === active && sources.filter(source => clothingLook(source) && source.visualReviewPassed === true && matchesGarmentRoute(source,route) && source.imageSha256 === row.sourceImageSha256 && source.selectionBindingSha256 === row.sourceSelectionBindingSha256 && (source.castId || null) === active).length === 1);
        return matches.length === 1 ? matches[0]:null;
      }
      function priorCompleteStylingLook(key) {
        const refined = refinedStylingLook(key); if (refined) return refined;
        const route = stylingRoute(key); if (!route) return null;
        const castId = stylingAssets.activeCasts[route.modelId]; if (!castId) return null;
        const matches = stylingAssets.completeLooks.filter(row => clothingLook(row) && row.visualReviewPassed === true && row.renderMode === 'complete-on-model' && row.castId === castId && matchesGarmentRoute(row,route));
        return matches.length === 1 ? matches[0]:null;
      }
      function realismSource(key) {
        const route = stylingRoute(key); if (!route) return null;
        const complete = priorCompleteStylingLook(key); if (complete) return complete;
        if (stylingAssets.activeCasts[route.modelId]) return null;
        const rows = stylingAssets.looks.filter(row => clothingLook(row) && row.visualReviewPassed === true && matchesGarmentRoute(row,route));
        return rows.length === 1 ? rows[0]:null;
      }
      function realismStylingLook(key) {
        const route = stylingRoute(key); if (!route) return null;
        const matches = stylingAssets.realismLooks.filter(row => {
          if (!(clothingLook(row) && row.visualReviewPassed === true && row.reviewBindingValidated === true && row.sourceVersion === 5 && row.sourceManifestSha256 === stylingAssets.realismSourceManifestSha256 && row.renderMode === 'complete-on-model' && matchesGarmentRoute(row,route))) return false;
          const source = realismSource(key);
          return source && row.sourceImageSha256 === source.imageSha256 && row.sourceSelectionBindingSha256 === source.selectionBindingSha256 && row.sourceRecipeSha256 === source.recipeSha256 && (row.sourceCastId || null) === (source.castId || null);
        });
        return matches.length === 1 ? matches[0]:null;
      }
      function completeStylingLook(key) {
        return realismStylingLook(key) || priorCompleteStylingLook(key);
      }
      function stylingLook(key) {


        return completeStylingLook(key) || realismSource(key);
      }

      function renderPreview() {
        const selectedPieces = pieces();
        elements['product-composition'].replaceChildren();
        Object.values(selectedPieces).filter(Boolean).forEach(addComposition);
        elements['reference-photos'].replaceChildren(...[...elements['product-composition'].children].map(node => node.cloneNode(true)));
        const look = stylingLook(state);
        resetPreviewCrop();
        if (look) {
          elements.canvas.dataset.render = 'model'; elements['product-composition'].hidden = true;
          elements['preview-frame'].hidden = false; elements['preview-photo'].hidden = false;
          elements['preview-frame'].dataset.cohort = state.cohort;
          elements['preview-photo'].src = look.image;
          elements['preview-photo'].alt = `${groupNames[state.group]}, ${state.cohort.replace(/-/g,' ')}, ${state.build} build: ${look.colour} ${look.outfit === 'crew-set' ? 'sweatshirt':look.outfit === 'robe' ? 'changing robe':'T-shirt'}${look.bottomColour ? ` with ${look.bottomColour} sweatpants`:''}. Approximate styling.`;
          setText(elements['canvas-label'],'Approximate styling');
          const single = ['tshirt','sweatshirt','pants'].includes(state.outfit) ? 'The surrounding outfit provides styling context; only your selected piece is included. ':'';
          setText(elements['preview-status'],single + 'Garment size choices update the measurements below. This illustration does not predict personal fit.');
        } else showProductFallback('Product photographs for this combination.');
        setText(elements['model-status'],'Choose a model build to explore the look. Garment sizes are selected separately.');
        setText(elements['coverage-note'],'Product colours can vary with lighting and screen settings. Compare the current product photographs for close details.');
        const colour = selectedColour(selectedPieces.top); const bottomColour = state.outfit === 'tshirt-pants' && selectedColour(selectedPieces.bottom);
        setText(elements['look-heading'],`${groupNames[state.group]} · ${colour && (colour.label || colour.id) || 'Your selected pieces'}${bottomColour ? ` / ${bottomColour.label || bottomColour.id}`:''}`);
      }

      function renderSizes() {
        const {top,bottom} = pieces(); setText(elements['top-name'],top && top.title || 'Garment information unavailable'); setText(elements['top-label'],['set','sweatshirt'].includes(state.outfit) ? 'Sweatshirt size':state.outfit === 'robe' ? 'Changing robe size':state.outfit === 'pants' ? 'Sweatpants size':'T-shirt size'); state.topSize = sizeOptions(elements['top-size'],top,state.topSize);
        elements['bottom-card'].hidden = !bottom; elements['bottom-size'].required = Boolean(bottom); elements['garment-size-row'].style.gridTemplateColumns = bottom ? '':'1fr';
        if (bottom) { setText(elements['bottom-name'],bottom.title); state.bottomSize = sizeOptions(elements['bottom-size'],bottom,state.bottomSize); } else { state.bottomSize = ''; elements['bottom-size'].value = ''; elements['bottom-size'].disabled = true; }
        updateMeasurements();
      }
      function updateMeasurements() {
        const {top,bottom} = pieces(); const topResult = renderMeasurements(top,state.topSize,elements['top-measurements']); const bottomResult = bottom ? renderMeasurements(bottom,state.bottomSize,elements['bottom-measurements']):null;
        const selected = [topResult,bottomResult].filter(Boolean); const complete = selected.every(result => result.complete);
        setText(elements['measurement-status'],complete ? 'Supplied garment measurements are shown for your selected sizes.':selected.some(result => result.selected && !result.complete) ? 'Some measurements are unavailable for this size. Please check the product page before choosing.':'Choose a garment size to see the supplied measurements.');
        const notes = [...new Set(selected.flatMap(result => Array.isArray(result.notes) ? result.notes:[String(result.notes || '')]).filter(Boolean))];
        setText(elements['measurement-notes'],notes.length ? notes.join(' '):'Measurements are taken from the manufacturer’s garment charts. Size selection is separate from the styling image.');
        updateReadiness();
      }
      function updateReadiness() {
        const {top,bottom} = pieces(); const ready = Boolean(top && sizeValues(selectedColour(top)).includes(state.topSize) && (!bottom || sizeValues(selectedColour(bottom)).includes(state.bottomSize)));
        elements['review-button'].disabled = !ready; setText(elements['selection-status'],ready ? 'Your sizes are selected. Review your pieces whenever you are ready.':'Choose the garment sizes to review your selection.');
      }
      function render() { const youth = state.group === 'boys' || state.group === 'girls'; configureBuilds(youth); elements['cohort-field'].hidden = !youth; elements['robe-option'].hidden = youth; elements['robe-option'].disabled = youth; if (youth && state.outfit === 'robe') { state.outfit = 'set'; elements.outfit.value = 'set'; } updateColours(); renderSizes(); renderPreview(); }
      scope.querySelectorAll('[data-group]').forEach(button => button.addEventListener('click',() => { if (state.group === button.dataset.group) return; state.group = button.dataset.group; state.cohort = (state.group === 'boys' || state.group === 'girls') ? elements.cohort.value:'adult'; state.colour = ''; state.bottomColour = ''; state.topSize = ''; state.bottomSize = ''; scope.querySelectorAll('[data-group]').forEach(other => other.setAttribute('aria-pressed',String(other === button))); render(); }));
      scope.querySelectorAll('[data-build]').forEach(button => button.addEventListener('click',() => { state.build = button.dataset.build; scope.querySelectorAll('[data-build]').forEach(other => other.setAttribute('aria-pressed',String(other === button))); renderPreview(); }));
      elements.cohort.addEventListener('change',() => { state.cohort = elements.cohort.value; renderPreview(); });
      elements.outfit.addEventListener('change',() => { state.outfit = elements.outfit.value; state.colour = ''; state.bottomColour = ''; state.topSize = ''; state.bottomSize = ''; render(); });
      elements['top-size'].addEventListener('change',() => { state.topSize = elements['top-size'].value; updateMeasurements(); });
      elements['bottom-size'].addEventListener('change',() => { state.bottomSize = elements['bottom-size'].value; updateMeasurements(); });
      elements.units.addEventListener('change',() => { state.unit = elements.units.value; updateMeasurements(); });
      elements['preview-photo'].addEventListener('error',() => { showProductFallback('Product photographs for this combination.'); });
      function selectionEntries() { const {top,bottom} = pieces(); return [{product:top,size:state.topSize},...(bottom ? [{product:bottom,size:state.bottomSize}]:[])].filter(entry => entry.product).map(entry => ({...entry,colour:selectedColour(entry.product)})); }
      scope.getElementById('selection-form').addEventListener('submit',event => {
        event.preventDefault(); if (elements['review-button'].disabled || !event.currentTarget.reportValidity()) return;
        const entries = selectionEntries(); elements['review-list'].replaceChildren();
        entries.forEach(entry => { const item = document.createElement('li'); item.className = 'review-item'; item.textContent = entry.product.title; const detail = document.createElement('small'); const colour = selectedColour(entry.product); detail.textContent = `${colour && (colour.label || colour.id) || 'Colour pending'} · ${entry.size}`; item.append(detail); elements['review-list'].append(item); });
        setText(elements['review-copy'],`${groupNames[state.group]} collection${state.cohort !== 'adult' ? ` · ${state.cohort.replace('-', ' ')} model group`:''} · ${state.build[0].toUpperCase()}${state.build.slice(1)} model build. Your garment sizes are independent of this visual preference.`); elements['review-dialog'].showModal();
      });
      [scope.getElementById('close-review'),scope.getElementById('continue-editing')].forEach(button => button.addEventListener('click',() => elements['review-dialog'].close()));
      elements['review-dialog'].addEventListener('close',() => elements['review-button'].focus());
      host.wardrobe = Object.freeze({version:7,scope:'clothing-only',realismStylingLook,realismSource,priorCompleteStylingLook,stylingAssets,stylingLook,stylingRoute,completeStylingLook,refinedStylingLook,getState:() => ({...state}),getSelectionEntries:selectionEntries,catalog,measurementCatalog,previewCatalog,localAssetPath});
      render();
    })();
  

}
